import React, { useEffect, useState } from "react";
import { isArray, isEmpty } from "lodash";
import { push } from "connected-react-router";
import { useSelector, useDispatch } from "react-redux";

import {
    Grid,
    Segment,
    Message,
    Button,
    Dimmer,
    Loader,
    Divider,
    Header,
    Icon,
    Image,
    Modal,
} from "semantic-ui-react";

import InvoiceComponentStatus from "../components/status";
import InvoiceComponentTimestamp from "../components/timestamp";
import InvoiceComponentDeliveryBlock from "../components/delivery-block";
import InvoiceBuilderContainerCustomer from "./customer.container";
import InvoiceComponentProductList from "../components/product-list";
import InvoiceBuilderComponentEditor from "./product-editor.component";
import InvoiceComponentCompanyHeader from "../components/company-header";
import CompanyLogoGalleryContainer from "../../company/logo/gallery.container";

import {
    resetInvoiceBuilder,
    changeInvoiceBuilderQty,
    addInvoiceBuilderProduct,
    changeInvoiceBuilderNote,
    changeInvoiceBuilderDeliveryType,
    changeInvoiceBuilderFulfillmentNote,
    cloneLastInvoiceBuilder,
    changeInvoiceBuilderDate,
    removeInvoiceBuilderItem,
    changeInvoiceBuilderLogo,
    saveInvoiceBuilderInvoice,
    changeInvoiceBuilderPrice,
    changeInvoiceBuilderStatus,
    selectInvoiceBuilderProduct,
    toggleInvoiceBuilderTaxable,
    createInvoiceBuilderInvoice,
    showInvoiceBuilderInlineEditor,
    hideInvoiceBuilderInlineEditor,
    changeInvoiceBuilderInlineEditor,
    changeShowInvoiceBuilderLogoPicker,
    removeInvoiceBuilderStampRoll,
    beginInvoiceBuilderStampRollEdit,
    cancelInvoiceBuilderStampForm,
    getInvoiceStampsInterval,
} from "./actions";
import { InvoiceStatuses } from "utilities/dist/invoice/constants";
import { DeliveryTypes } from "utilities/dist/order/constants";

import { toast } from "react-semantic-toasts";
import { changeInvoiceGridState } from "../grid/actions";
import { clearProductSearch } from "../../product/search/actions";
import { computeTotalFromItems } from "../helpers";
import { isBeer, isLiquor } from "utilities/dist/helpers";
import InvoiceBuilderFooterContent from "./footer-content.component";
import InvoiceConverterUploaderContainer from "../converter/uploader.container";
import { loadCompanyLogos } from "../../company/logo/actions";
import {
    CustomerInvoiceNumberingFeature,
    InternalSkuFeature,
    InventoryFeatureDetails,
    isFeatureActive,
} from "../../company/feature/helpers";
import { Link } from "react-router-dom";
import InvoiceBuilderContainerConfirmToggle from "./confirm-toggle.container";
import {
    addItemLogProduct,
    changeItemLogInlineEditor,
    hideItemLogInlineEditor,
    itemLogRemoveItem,
    resetItemLog,
    showItemLogInlineEditor,
} from "../item-log/actions";
import { useProductEditorErrors } from "./product-editor-error";
import DuplicateErrorModal from "./duplicate-error.modal";
import { buildInvoiceNumberWithBeer } from "utilities/dist/invoice/helpers";
import { InvoiceRecommendationButtonContainer } from "../recommendation/button.container";
import { InvoiceComponentFintechLabel } from "../components/fintech-label";
import { formatAmount, calculateSpiritVolume } from "utilities/dist/helpers";
import { getTaxRate } from "utilities/dist/company/helpers";
import { InvoiceComponentCODLabel } from "../components/cod-label";

const InvoiceBuilderContainer = () => {
    const {
        inlineEditorItemId,
        inlineEditorFields,
        showLogoPicker,
        cloningLast,
        invoiced_at,
        isLoading,
        isSaving,
        customer,
        order_number,
        logo_id,
        invoice,
        company,
        stamps,
        editingStamp,
        editor,
        status,
        logos,
        errors,
        items,
        note,
        delivery_type,
        fulfillment_note,
        orderNotes,
        order_delivery_type,
        orderNumber,
        itemLogInlineEditorFields,
        itemLogInlineEditorItemId,
        itemLog,
        userRole,
    } = useSelector(({ invoiceBuilder, auth, companyLogo, itemLog }) => ({
        company: auth.user.company,
        userRole: auth.user.role,

        logos: companyLogo.logos,

        inlineEditorItemId: invoiceBuilder.inlineEditorItemId,
        inlineEditorFields: invoiceBuilder.inlineEditorFields,
        showLogoPicker: invoiceBuilder.showLogoPicker,
        cloningLast: invoiceBuilder.cloningLast,
        invoiced_at: invoiceBuilder.invoiced_at,
        isLoading: invoiceBuilder.isLoading,
        isSaving: invoiceBuilder.isSaving,
        customer: invoiceBuilder.customer,
        order_number: invoiceBuilder.order_number,
        logo_id: invoiceBuilder.logo_id
            ? invoiceBuilder.logo_id
            : auth.user.company?.default_logo_id,
        invoice: invoiceBuilder.invoice,
        stamps: invoiceBuilder.stamps,
        editingStamp: invoiceBuilder.editingStamp,
        editor: invoiceBuilder.editor,
        errors: invoiceBuilder.errors,
        status: invoiceBuilder.status,
        items: invoiceBuilder.items,
        note: invoiceBuilder.note,
        delivery_type: invoiceBuilder.delivery_type,
        fulfillment_note: invoiceBuilder.fulfillment_note,
        orderNotes: invoiceBuilder.orderNotes ?? [],
        order_delivery_type: invoiceBuilder.order_delivery_type ?? null,
        orderNumber: invoiceBuilder.order_number,
        itemLogInlineEditorFields: itemLog.inlineEditorFields,
        itemLogInlineEditorItemId: itemLog.inlineEditorItemId,
        itemLog: itemLog.items,
    }));
    const [showModal, setShowModal] = useState(false);

    const dispatch = useDispatch();
    useEffect(() => {
        if (logos?.length > 0) dispatch(loadCompanyLogos());
    }, []);

    const logo = logos.find(({ id }) => logo_id === id);
    const isCancelled = !!invoice && invoice.status === InvoiceStatuses.CANCELLED;
    const itemsArr = Object.values(items);
    const itemLogArr = Object.values(itemLog);
    const totals = computeTotalFromItems(
        itemsArr,
        invoice?.id ? invoice.tax_total / invoice.sub_total : getTaxRate(company)
    );
    const canCloneLast = (!invoice || !invoice.id) && isEmpty(itemsArr);
    const canSeeRecommendation = !invoice?.id && customer;
    const hasBeer = !!itemsArr.find(i => isBeer(i));
    // by doing a !hasBeer first, we make sure the second check is only performed if no beer item has been added
    // saves us an entire loop through all the items in the invoice
    const hasLiquor = !!itemsArr.find(i => isLiquor(i));
    const orderNumberFromInvoice = invoice?.orders.length ? invoice?.orders[0].order_number : "";
    const isCustomerInvoiceNumberingActive = isFeatureActive(
        company,
        CustomerInvoiceNumberingFeature
    );

    const showCostError = editor.product && editor.price < editor.product.last_purchase_price;
    const disableStampEditIcons = !isEmpty(editingStamp);
    const isDelivery = delivery_type === DeliveryTypes.DELIVERY;
    const orderWasDelivery = order_delivery_type === DeliveryTypes.DELIVERY;
    const deliveryNoteRequired = isDelivery && !orderWasDelivery;
    const minSpiritVolume = Number(company?.min_spirit_volume_ml ?? 0);
    const spiritVolume = calculateSpiritVolume(itemsArr);
    const showSpiritVolumeWarning =
        isDelivery &&
        hasLiquor &&
        totals.total >= 0 &&
        minSpiritVolume > 0 &&
        spiritVolume < minSpiritVolume;

    const successToast = message => toast({ type: "success", icon: "checkmark", title: message });
    const cloneLast = () => dispatch(cloneLastInvoiceBuilder(customer));
    const handleCancel = () => {
        dispatch(resetInvoiceBuilder());
        dispatch(resetItemLog());
    };
    const confirmSaveInvoiceData = (saveFunc, invoiceData) => {
        const invoiceExists = !!invoiceData.id;
        dispatch(
            saveFunc({
                ...invoiceData,
                customer,
                orderNumber: order_number,
                stamps,
                status,
                invoiced_at,
                note,
                delivery_type,
                fulfillment_note,
                logo_id,
                items: itemsArr,
                itemLog: itemLogArr,
            })
        ).then(inv => {
            if (isArray(inv)) {
                if (inv.length > 1) {
                    successToast(invoiceExists ? "Invoice saved" : "Invoice created");
                    dispatch(
                        changeInvoiceGridState({
                            filterModel: {
                                number: {
                                    type: "inRange",
                                    filterType: "number",
                                    filter: inv[0].number,
                                    filterTo: inv[1].number,
                                },
                            },
                        })
                    );
                    return dispatch(push(`/invoice`));
                } else {
                    successToast(invoiceExists ? "Invoice saved" : "Invoice created");
                    return dispatch(
                        push(
                            `/invoice/${buildInvoiceNumberWithBeer(
                                inv[0],
                                isCustomerInvoiceNumberingActive
                            )}`
                        )
                    );
                }
            } else if (!isEmpty(inv)) {
                successToast(invoiceExists ? "Invoice saved" : "Invoice created");
                return dispatch(
                    push(
                        `/invoice/${buildInvoiceNumberWithBeer(
                            inv,
                            isCustomerInvoiceNumberingActive
                        )}`
                    )
                );
            }
        });
    };
    const priceZeroItems = itemsArr.filter(item => Number(item.price) === 0);

    const showDeliveryNoteWarning = () => {
        if (deliveryNoteRequired && !(fulfillment_note && fulfillment_note.trim())) {
            toast({
                type: "warning",
                icon: "warning sign",
                title: "Delivery note required",
                description: "Please add a delivery note when fulfillment is Delivery.",
            });
            return true;
        }
        return false;
    };

    const saveInvoice = invoiceData => {
        if (showDeliveryNoteWarning()) return;
        let saveFunc;
        if (!priceZeroItems.length) {
            saveFunc = invoiceData.id ? saveInvoiceBuilderInvoice : createInvoiceBuilderInvoice;

            confirmSaveInvoiceData(saveFunc, invoiceData);
        } else {
            setShowModal(true);
        }
    };
    const saveOnConfirm = (ignoreDuplicate = false) => {
        const saveFunc = invoice?.id ? saveInvoiceBuilderInvoice : createInvoiceBuilderInvoice;

        confirmSaveInvoiceData(saveFunc, { ...invoice, ignoreDuplicate });
        setShowModal(false);
    };
    const handleToggleLogoPicker = showPicker =>
        dispatch(changeShowInvoiceBuilderLogoPicker(showPicker));
    const handleDateChange = invoiced_at => dispatch(changeInvoiceBuilderDate(invoiced_at));
    const handleStatusChange = status => dispatch(changeInvoiceBuilderStatus(status));
    const handleProductSelect = product => dispatch(selectInvoiceBuilderProduct(product));
    const changePrice = value => dispatch(changeInvoiceBuilderPrice(value));
    const changeNote = (e, { value }) => dispatch(changeInvoiceBuilderNote(value));
    const changeQty = (e, { value }) => dispatch(changeInvoiceBuilderQty(value));
    const toggleTaxable = () => dispatch(toggleInvoiceBuilderTaxable());
    const addProduct = () => {
        dispatch(addInvoiceBuilderProduct());
        dispatch(clearProductSearch());
        if (orderNumber) {
            dispatch(addItemLogProduct());
        }
    };
    const handleLogoChange = logo_id => {
        dispatch(changeInvoiceBuilderLogo(logo_id));
        handleToggleLogoPicker(false);
    };

    const handleHideInlineEditor = field => {
        dispatch(hideInvoiceBuilderInlineEditor(field));
        dispatch(hideItemLogInlineEditor(field));
    };

    const handleChangeInlineEditor = (field, value) =>
        dispatch(changeInvoiceBuilderInlineEditor(field, value));

    const handleChangeItemLogInlineEditor = (field, value) =>
        dispatch(changeItemLogInlineEditor(field, value));

    const handleShowItemLogInlineEditor = (item, field) =>
        dispatch(showItemLogInlineEditor(item, field));

    const handleShowInlineEditor = (item, field) =>
        dispatch(showInvoiceBuilderInlineEditor(item, field));

    const removeStamp = index => dispatch(removeInvoiceBuilderStampRoll(index));

    const editStamp = index => {
        const stampData = {
            from: stamps[index].from,
            dsCount: stamps[index].to - stamps[index].from + 1,
            reclaimableIntervals: stamps.map(stamp => ({
                from: stamp.from,
                to: stamp.to,
                roll_id: stamp.roll_id,
            })),
        };
        dispatch(beginInvoiceBuilderStampRollEdit(index));
        dispatch(getInvoiceStampsInterval({ ...stampData, isEditing: true })).catch(() => {
            dispatch(cancelInvoiceBuilderStampForm());
        });
    };
    const handleItemRemove = item => {
        dispatch(removeInvoiceBuilderItem(item));
        dispatch(itemLogRemoveItem(item));
    };
    const hasQuickActions =
        !orderNumber &&
        (canCloneLast || canSeeRecommendation || (canCloneLast && !!customer.fintech_option));

    const handleItemEdit = item => {
        handleShowItemLogInlineEditor(item, ["actions", "qty", "price"]);
        handleShowInlineEditor(item, ["price", "qty"]);
    };
    const isInternalSkuActive = isFeatureActive(company, InternalSkuFeature);
    const isInventoryActive = isFeatureActive(company, InventoryFeatureDetails);

    const { showTypeMisMatchError, errors: productEditorErrors } = useProductEditorErrors({
        hasBeer,
        item: editor,
        isInventoryActive,
        invoiceId: invoice?.id,
        totalItems: itemsArr.length,
        isCashOnDelivery: customer?.cash_on_delivery,
        isInvoiceBeer: invoice?.beer === true || invoice?.beer === "1",
    });

    const isEditable = [InvoiceStatuses.DRAFT, InvoiceStatuses.READY_FOR_PICK].includes(status);
    const isStampsOnlyMode = status === InvoiceStatuses.READY_FOR_TABC_STAMPS;

    useEffect(() => {
        if (!status) handleStatusChange(company.default_invoice_status);
    }, [status]);

    return (
        <Dimmer.Dimmable as={Grid} dimmed={isLoading || isSaving}>
            <Dimmer active={isLoading || isSaving} inverted>
                <Loader>Loading</Loader>
            </Dimmer>
            <Grid.Row>
                <Grid.Column textAlign="left" mobile={16} tablet={10} computer={10}>
                    <Modal
                        closeIcon
                        closeOnEscape={true}
                        open={showLogoPicker}
                        closeOnDimmerClick={true}
                        onClose={() => handleToggleLogoPicker(false)}
                    >
                        <Modal.Content>
                            <CompanyLogoGalleryContainer onSelect={handleLogoChange} />
                        </Modal.Content>
                    </Modal>

                    {!!logo ? (
                        <div className="bp-10">
                            <Image
                                floated="left"
                                className="clickable rm-10"
                                src={logo.path}
                                onClick={() => handleToggleLogoPicker(true)}
                                style={{
                                    maxWidth: "200px",
                                    maxHeight: "100px",
                                }}
                            />
                            <InvoiceComponentCompanyHeader company={company} />
                        </div>
                    ) : (
                        <>
                            <Header.Subheader
                                className="no-print clickable"
                                onClick={() => handleToggleLogoPicker(true)}
                                style={{
                                    marginLeft: "auto",
                                    marginRight: "auto",
                                }}
                            >
                                <Icon name="picture" /> Add Logo
                            </Header.Subheader>

                            <InvoiceComponentCompanyHeader company={company} />
                        </>
                    )}
                    {!!showModal && (
                        <InvoiceBuilderContainerConfirmToggle
                            saveOnConfirm={saveOnConfirm}
                            setShowModal={setShowModal}
                            zeroPricedItems={priceZeroItems}
                            saveInvoice={saveInvoice}
                        />
                    )}

                    <InvoiceComponentStatus
                        status={status}
                        //onChange={(e, { value }) => handleStatusChange(value)}
                    />
                    <InvoiceComponentTimestamp
                        invoiced_at={invoiced_at}
                        onChange={(e, { value }) => handleDateChange(value)}
                        editable={isEditable}
                    />
                    {!!company?.has_fintech && <InvoiceComponentFintechLabel customer={customer} />}
                    {!!company?.cash_on_delivery && (
                        <InvoiceComponentCODLabel customer={customer} />
                    )}
                </Grid.Column>
                <Grid.Column mobile={16} tablet={6} computer={6}>
                    <InvoiceBuilderContainerCustomer />
                    {!!orderNumber && (
                        <Header textAlign="right">
                            <Header.Subheader className="no-print">
                                Creating invoice from Order{" "}
                                <Link title="Order info" to={`/order/${orderNumber}`}>
                                    #{orderNumber}
                                </Link>
                            </Header.Subheader>
                        </Header>
                    )}
                </Grid.Column>
            </Grid.Row>

            {customer && customer.id && (
                <Grid.Row>
                    <Grid.Column width={16}>
                        <Segment attached>
                            <InvoiceComponentDeliveryBlock
                                delivery_type={delivery_type ?? "pickup"}
                                fulfillment_note={fulfillment_note ?? ""}
                                orderNotes={orderNotes ?? []}
                                order_delivery_type={order_delivery_type ?? null}
                                onChangeDeliveryType={value =>
                                    dispatch(changeInvoiceBuilderDeliveryType(value))
                                }
                                onChangeFulfillmentNote={value =>
                                    dispatch(changeInvoiceBuilderFulfillmentNote(value))
                                }
                                disabled={!isEditable}
                            />
                        </Segment>
                    </Grid.Column>
                </Grid.Row>
            )}

            {customer && customer.id && (
                <>
                    <Grid.Row verticalAlign="middle">
                        <Grid.Column width={16}>
                            {errors.length > 0 && (
                                <Message
                                    error
                                    header="Something Went Wrong"
                                    list={errors.length > 1 && errors}
                                    content={errors.length === 1 && errors}
                                />
                            )}
                            <Segment
                                disabled={isCancelled}
                                attached={!!productEditorErrors?.length || showCostError}
                            >
                                <Grid.Row className="ui grid computer only tablet only">
                                    <Grid.Column>
                                        {canCloneLast && !!customer.fintech_option && (
                                            <InvoiceConverterUploaderContainer />
                                        )}

                                        {canCloneLast && (
                                            <Button
                                                onClick={cloneLast}
                                                labelPosition="left"
                                                disabled={cloningLast}
                                                content="Replicate Last Invoice"
                                                icon={{
                                                    name: cloningLast ? "spinner" : "copy",
                                                    loading: cloningLast,
                                                }}
                                            />
                                        )}

                                        {canSeeRecommendation && (
                                            <InvoiceRecommendationButtonContainer
                                                customer={customer}
                                            />
                                        )}
                                    </Grid.Column>
                                </Grid.Row>
                                <Grid.Row className="ui grid mobile only" textAlign="center">
                                    <Grid.Column className="ui coloumn grid">
                                        <Button.Group className="ui grid" size="mini">
                                            {canCloneLast && (
                                                <Button
                                                    onClick={cloneLast}
                                                    icon={{
                                                        name: cloningLast ? "spinner" : "copy",
                                                        loading: cloningLast,
                                                    }}
                                                    content="Replicate Last Invoice"
                                                    disabled={cloningLast}
                                                />
                                            )}
                                        </Button.Group>
                                    </Grid.Column>
                                </Grid.Row>
                                {hasQuickActions && <Divider horizontal>Or</Divider>}

                                <InvoiceBuilderComponentEditor
                                    item={editor}
                                    customer={customer}
                                    onQtyChange={changeQty}
                                    onAddProduct={addProduct}
                                    onPriceChange={changePrice}
                                    onTaxableToggle={toggleTaxable}
                                    onProductChange={handleProductSelect}
                                    cantAdd={!!showTypeMisMatchError || !isEditable}
                                />
                            </Segment>
                            {(!!productEditorErrors.length || showCostError) && (
                                <Message
                                    color="yellow"
                                    attached="bottom"
                                    header="Please be aware"
                                    list={
                                        showCostError
                                            ? [
                                                  ...productEditorErrors,
                                                  `The cost of this product is ${formatAmount(
                                                      editor.product.last_purchase_price
                                                  )}, you sure you want to invoice this for ${formatAmount(
                                                      editor.price
                                                  )}?`,
                                              ]
                                            : productEditorErrors
                                    }
                                />
                            )}
                        </Grid.Column>

                        {itemsArr.length > 0 && (
                            <Grid.Column width={16}>
                                <div className="tp-15">
                                    <InvoiceComponentProductList
                                        isInternalSkuActive={isInternalSkuActive}
                                        orderNumber={
                                            orderNumber ? orderNumber : orderNumberFromInvoice
                                        }
                                        items={itemsArr}
                                        isEditable={isEditable}
                                        onItemEdit={handleItemEdit}
                                        onItemRemove={handleItemRemove}
                                        inlineEditorFields={inlineEditorFields}
                                        itemLogInlineEditorFields={itemLogInlineEditorFields}
                                        inlineEditorItemId={inlineEditorItemId}
                                        itemLogInlineEditorItemId={itemLogInlineEditorItemId}
                                        showSpiritVolumeWarning={showSpiritVolumeWarning}
                                        spiritVolume={spiritVolume}
                                        minSpiritVolume={minSpiritVolume}
                                        onShowInlineEditor={handleShowInlineEditor}
                                        onShowInItemLogInlineEditor={handleShowItemLogInlineEditor}
                                        onHideInlineEditor={handleHideInlineEditor}
                                        onChangeInlineEditor={handleChangeInlineEditor}
                                        onChangeItemLogInlineEditor={
                                            handleChangeItemLogInlineEditor
                                        }
                                        footerContent={
                                            <InvoiceBuilderFooterContent
                                                {...{
                                                    hasLiquor,
                                                    isCancelled,
                                                    note: !note.length ? "" : note,
                                                    invoice,
                                                    totals,
                                                    stamps,
                                                    status,
                                                    company,
                                                    delivery_type,
                                                    changeNote,
                                                    saveInvoice,
                                                    handleCancel,
                                                    removeStamp,
                                                    editStamp,
                                                    userRole,
                                                    editable: isEditable,
                                                    isStampsOnlyMode,
                                                    disableStampEditIcons,
                                                    isSaving,
                                                }}
                                            />
                                        }
                                    />
                                </div>
                            </Grid.Column>
                        )}
                    </Grid.Row>
                    <DuplicateErrorModal
                        customer={customer}
                        handleProceed={() => saveOnConfirm(true)}
                    />
                </>
            )}
        </Dimmer.Dimmable>
    );
};

export default InvoiceBuilderContainer;
